<script setup lang="ts">
  import { computed, getCurrentInstance, onBeforeUnmount, ref } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import type { TMenuImportSaveState } from '@/composition/menu-import/menu-import-context';
  import { useModal } from '@/composition/use-modal';
  import {
    PUBLISH_DIALOG_MODAL,
    publishLines,
  } from '@/domain/menu-import/publish-dialog';
  import type {
    IPublishDialogModel,
    IPublishLine,
    TPublishAttempt,
    TPublishDialogResult,
  } from '@/domain/menu-import/publish-dialog';

  /**
   * «¿Publicamos su menú?» (plan §4.7): how many dishes the diners will see
   * and where, what is left to check, hidden dishes, allergens, the menu's
   * language and currency. An update (plan §3.6) asks «¿Publicamos los
   * cambios?» and says what changes: prices, dishes added, hidden, shown
   * again, and the menu's dishes left as they are. «Publicar» awaits
   * `publish()` with its own busy state: a failure stays here with an inline
   * message, anything else closes the dialog with the result
   * (`showDynamicDialog<TPublishDialogResult>`).
   * While changes are still being saved (or their save failed) «Publicar»
   * waits, with the save state and «Reintentar»: it never queues behind a
   * save that may not go out. «Seguir revisando» always closes the dialog.
   */
  const props = defineProps<{
    model: IPublishDialogModel;
    publish: () => Promise<TPublishAttempt>;
    /** The session's save state (read live). */
    saveState: () => TMenuImportSaveState;
    /** «Reintentar» for saves that ran out of retries. */
    retrySaves: () => void;
  }>();
  const emit = defineEmits<{ (e: 'close'): void }>();
  const proxy = getCurrentInstance()!.proxy;
  const tr = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  const trc = (key: string, count: number, params?: Record<string, unknown>) =>
    String(proxy.$tc(key, count, params));
  const modals = useModal();

  const busy = ref(false);
  /** The inline message after a press of «Publicar» that did not publish. */
  const error = ref<'failed' | 'nothingVisible' | null>(null);
  let alive = true;

  const lines = computed(() => publishLines(props.model));
  const update = computed(() => props.model.update !== null);
  /** Changes not saved yet: publishing now would leave them out. */
  const saveState = computed(() => props.saveState());
  const waitingForSaves = computed(() => saveState.value !== 'saved');
  const saveText = computed(() => {
    switch (saveState.value) {
      case 'saving':
        return tr('storeSetup.saving');
      case 'offline':
        return tr('storeSetup.savingOffline');
      case 'failed':
        return tr('menuImport.errors.saveFailed');
      default:
        return null;
    }
  });
  const errorText = computed(() => {
    if (error.value === 'nothingVisible')
      return tr('menuImport.publish.nothingVisible');
    if (error.value === 'failed') return tr('menuImport.publish.error');
    return null;
  });

  const confirmText = computed(() => {
    if (busy.value) return tr('menuImport.publish.publishing');
    return update.value
      ? tr('menuImport.update.publish')
      : tr('menuImport.publish.confirm');
  });

  function lineText(line: IPublishLine): string {
    return line.count === null
      ? tr(line.key, line.params)
      : trc(line.key, line.count, line.params);
  }

  function submit(result: TPublishDialogResult) {
    void modals.dialogSubmit(PUBLISH_DIALOG_MODAL, result);
  }

  /** «Revisarlos»: back to the list, showing the dishes to check. */
  function review() {
    if (!busy.value) submit('review');
  }

  /** Always possible: a publish on its way still counts (the flow awaits it). */
  function cancel() {
    emit('close');
  }

  async function confirm() {
    if (busy.value || waitingForSaves.value) return;
    busy.value = true;
    error.value = null;
    let attempt: TPublishAttempt;
    try {
      attempt = await props.publish();
    } catch (failure) {
      console.error('[menu-import] publish failed', failure);
      attempt = 'failed';
    }
    if (!alive) return;
    if (attempt === 'failed' || attempt === 'nothingVisible') {
      busy.value = false;
      error.value = attempt;
      return;
    }
    // Closes the dialog: nothing local is touched after this.
    submit(attempt);
  }

  onBeforeUnmount(() => {
    alive = false;
  });
</script>

<template>
  <div class="v2-menu-publish-dialog">
    <header class="dialog-head">
      <h2 class="dialog-title">
        {{
          update
            ? $t('menuImport.update.dialogTitle')
            : $t('menuImport.publish.title')
        }}
      </h2>
    </header>
    <div class="dialog-body">
      <ul class="publish-lines">
        <li
          v-for="line in lines"
          :key="line.id"
          class="publish-line"
          :class="`tone-${line.tone}`"
        >
          <PosUiIcon :name="line.icon" class="line-icon" />
          <span class="line-text">
            {{ lineText(line) }}
            <button
              v-if="line.id === 'unreviewed'"
              type="button"
              class="line-link"
              :disabled="busy"
              @click="review"
            >
              {{ $tc('menuImport.publish.reviewThem', model.unreviewed) }}
            </button>
          </span>
        </li>
      </ul>
      <p v-show="errorText !== null" class="publish-error" role="alert">
        <PosUiIcon name="alert-circle" class="error-icon" />
        <span>{{ errorText }}</span>
      </p>
      <!-- v-show: the line (and its «Reintentar») never unmounts under the focus. -->
      <div
        v-show="!busy && saveText !== null"
        class="publish-saving"
        :class="`is-${saveState}`"
        role="status"
      >
        <span
          v-if="saveState === 'saving'"
          class="saving-spinner"
          aria-hidden="true"
        />
        <PosUiIcon
          v-else
          :name="saveState === 'offline' ? 'wifi-off' : 'alert-circle-outline'"
          class="saving-icon"
        />
        <span class="saving-text">{{ saveText }}</span>
        <PosUiButton
          v-show="saveState === 'failed'"
          class="saving-retry"
          color="neutral"
          variant="outline"
          size="sm"
          @click="retrySaves()"
          >{{ $t('menuImport.tray.retry') }}</PosUiButton
        >
      </div>
    </div>
    <footer class="dialog-foot">
      <PosUiButton color="neutral" variant="outline" @click="cancel">{{
        $t('menuImport.publish.cancel')
      }}</PosUiButton>
      <!-- busy stays on until the dialog closes: the spinner never flips during its teardown. -->
      <PosUiButton
        :loading="busy"
        :disabled="!busy && waitingForSaves"
        @click="confirm"
        >{{ confirmText }}</PosUiButton
      >
    </footer>
  </div>
</template>

<style scoped lang="scss">
  .v2-menu-publish-dialog {
    @apply tw-flex tw-flex-col tw-bg-white;
    max-height: 85vh;
    .dialog-head {
      @apply tw-shrink-0 tw-px-5 tw-pb-2 tw-pt-5;
    }
    .dialog-title {
      @apply tw-m-0 tw-text-lg tw-font-semibold tw-text-neutral-900;
    }
    .dialog-body {
      @apply tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-gap-4 tw-overflow-y-auto tw-px-5 tw-py-2;
      overscroll-behavior: contain;
    }
    .publish-lines {
      @apply tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-2.5 tw-p-0;
    }
    .publish-line {
      @apply tw-flex tw-items-start tw-gap-2.5 tw-text-sm tw-text-neutral-700;
      .line-icon {
        @apply tw-mt-0.5 tw-shrink-0 tw-text-base tw-text-neutral-500;
      }
      .line-text {
        @apply tw-min-w-0 tw-break-words;
      }
      &.tone-warning {
        @apply tw-text-amber-700;
        .line-icon {
          @apply tw-text-amber-600;
        }
      }
    }
    // 44 px tall for touch, sitting on the text's line.
    .line-link {
      @apply tw-ml-1 tw-inline-flex tw-min-h-11 tw-cursor-pointer tw-items-center tw-border-0 tw-bg-transparent tw-p-0 tw-align-middle tw-text-sm tw-font-medium tw-text-neutral-900 tw-underline tw-underline-offset-4;
      margin-top: -12px;
      margin-bottom: -12px;
      &:focus-visible {
        @apply tw-rounded tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
      &:disabled {
        @apply tw-cursor-not-allowed tw-text-neutral-400;
      }
    }
    .publish-error {
      @apply tw-m-0 tw-flex tw-items-start tw-gap-2 tw-rounded-lg tw-border tw-border-solid tw-border-red-200 tw-bg-red-50 tw-px-3 tw-py-2.5 tw-text-sm tw-text-red-700;
      .error-icon {
        @apply tw-mt-0.5 tw-shrink-0 tw-text-base;
      }
    }
    // Why «Publicar» waits: changes still on their way, or a save to retry.
    .publish-saving {
      @apply tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1 tw-rounded-lg tw-border tw-border-solid tw-border-neutral-200 tw-bg-canvas tw-px-3 tw-py-2 tw-text-sm tw-text-neutral-700;
      &.is-failed {
        @apply tw-border-red-200 tw-bg-red-50 tw-text-red-700;
      }
      .saving-icon {
        @apply tw-shrink-0 tw-text-base;
      }
      .saving-spinner {
        @apply tw-h-3.5 tw-w-3.5 tw-shrink-0 tw-animate-spin tw-rounded-full tw-border-2 tw-border-solid tw-border-neutral-300 tw-border-t-neutral-600;
      }
      .saving-text {
        @apply tw-min-w-0 tw-flex-1;
      }
      .saving-retry {
        @apply tw-min-h-11;
      }
    }
    .dialog-foot {
      @apply tw-flex tw-shrink-0 tw-flex-wrap tw-justify-end tw-gap-2 tw-px-5 tw-pb-5 tw-pt-3;
    }
  }

  // Phones: the two buttons share the row, «Publicar» last.
  @media (max-width: 479px) {
    .v2-menu-publish-dialog .dialog-foot > * {
      @apply tw-flex-1;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .v2-menu-publish-dialog .publish-saving .saving-spinner {
      @apply tw-animate-none;
    }
  }
</style>
